/**
 * The "Add a rule" form is a markdown generator — rules are never stored as
 * fields, only as sections of a policy document. These are the pure functions
 * that turn the four inputs into that section and splice it into a document.
 */

export interface RuleFields {
  /** The situation the rule applies to. Doubles as the section heading. */
  whenHelpingWith: string;
  neverInstruct: string;
  instead: string;
  /** Governing policy / SOP reference. Optional — the line is dropped when blank. */
  authority: string;
}

export const emptyRuleFields: RuleFields = {
  whenHelpingWith: '',
  neverInstruct: '',
  instead: '',
  authority: '',
};

/** How many rule sections (## headings) a document already holds. */
export const countRuleSections = (document: string): number =>
  (document.match(/^##\s/gm) ?? []).length;

/**
 * One rule as a markdown section, titled by the caller ("Rule 3") — repeating
 * the situation as the heading read as a duplicate. No trailing newline —
 * `appendRule` spaces it.
 */
export const ruleToMarkdown = (fields: RuleFields, title: string): string => {
  const authority = fields.authority.trim();
  const lines = [
    `## ${title}`,
    `**When helping with:** ${fields.whenHelpingWith.trim()}`,
    `**Never instruct:** ${fields.neverInstruct.trim()}`,
    `**Instead:** ${fields.instead.trim()}`,
  ];
  if (authority) lines.push(`**Authority:** ${authority}`);
  return lines.join('\n');
};

/**
 * Append a rule section to a document, blank-line separated. Trailing
 * whitespace on the existing document is normalized away first so repeated
 * appends can't accumulate blank lines.
 */
export const appendRule = (document: string, rule: string): string => {
  const base = document.replace(/\s+$/, '');
  return base.length > 0 ? `${base}\n\n${rule}` : rule;
};

/** Authority is the only optional field — the other three make the rule. */
export const isCompleteRule = (fields: RuleFields): boolean =>
  fields.whenHelpingWith.trim().length > 0 &&
  fields.neverInstruct.trim().length > 0 &&
  fields.instead.trim().length > 0;
